import React from 'react';
import { Plus, Trash2, Edit2, AlertCircle, Coins, Wallet, Gift, Scissors } from 'lucide-react';
import Modal from './Modal';
import { useLanguage } from '../context/LanguageContext';

const typeMeta = {
  advance: { label: 'Advance', icon: Coins, sign: '-', color: 'text-orange-700', bg: 'bg-orange-100' },
  salary: { label: 'Salary Paid', icon: Wallet, sign: '-', color: 'text-green-700', bg: 'bg-green-100' },
  bonus: { label: 'Bonus', icon: Gift, sign: '+', color: 'text-blue-700', bg: 'bg-blue-100' },
  deduction: { label: 'Deduction', icon: Scissors, sign: '-', color: 'text-red-700', bg: 'bg-red-100' }
};

function Row({ label, value, emphasis = false, muted = false }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={`text-sm ${muted ? 'text-gray-500' : 'text-gray-700'}`}>{label}</span>
      <span className={`${emphasis ? 'text-base font-bold' : 'text-sm font-medium'} text-gray-900`}>
        Rs. {value.toFixed(2)}
      </span>
    </div>
  );
}

function StaffLedgerModal({
  isOpen,
  onClose,
  staff,
  loading,
  onAddTransaction,
  onEditTransaction,
  onDeleteTransaction
}) {
  const { t } = useLanguage();
  const ledger = staff?.ledger;
  const transactions = staff?.transactions || [];
  const balance = ledger?.balance ?? 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={staff ? `${staff.name} - Account` : 'Account'} size="2xl">
      <div className="p-4 sm:p-6 space-y-5">
        {loading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('Loading account...')}</p>
          </div>
        ) : !staff ? (
          <div className="text-center py-10">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-3" />
            <p className="text-red-600">{t('Could not load this account.')}</p>
          </div>
        ) : (
          <>
            {/* Balance banner */}
            <div
              className={`rounded-xl p-5 border ${
                balance > 0
                  ? 'bg-green-50 border-green-200'
                  : balance < 0
                  ? 'bg-red-50 border-red-200'
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <p className="text-sm text-gray-600">
                {balance > 0
                  ? 'You still owe'
                  : balance < 0
                  ? 'Paid ahead - they owe you'
                  : 'All settled up'}
              </p>
              <p
                className={`text-3xl font-bold mt-1 ${
                  balance > 0 ? 'text-green-700' : balance < 0 ? 'text-red-700' : 'text-gray-900'
                }`}
              >
                Rs. {Math.abs(balance).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {staff.position ? `${staff.position} • ` : ''}Rs. {staff.monthlySalary.toFixed(2)}/month • joined{' '}
                {new Date(staff.joinDate).toLocaleDateString()}
                {staff.endDate ? ` • left ${new Date(staff.endDate).toLocaleDateString()}` : ''}
              </p>
            </div>

            {/* Breakdown */}
            {ledger && (
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">{t('How this was worked out')}</h4>
                <div className="divide-y divide-gray-100">
                  <Row label={t('Salary earned so far')} value={ledger.accruedSalary} />
                  <Row label={t('Bonuses')} value={ledger.totalBonus} />
                  <Row label={t('Deductions')} value={ledger.totalDeduction} muted />
                  <Row label={t('Advances taken')} value={ledger.totalAdvance} muted />
                  <Row label={t('Salary paid')} value={ledger.totalSalaryPaid} muted />
                </div>
                <div className="border-t-2 border-gray-200 mt-2 pt-2">
                  <Row label={balance >= 0 ? 'Balance owed' : 'Overpaid by'} value={Math.abs(balance)} emphasis />
                </div>
              </div>
            )}

            {/* History */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-base font-semibold text-gray-900">{t('History')}</h4>
                <button
                  onClick={onAddTransaction}
                  className="px-4 py-2 min-h-[40px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center text-sm font-medium"
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  {t('Record Entry')}
                </button>
              </div>

              {transactions.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-gray-300 rounded-xl">
                  <p className="text-gray-500 text-sm">{t('No entries yet.')}</p>
                  <p className="text-gray-400 text-xs mt-1">
                    {t('Record an advance or a salary payment to start the account.')}
                  </p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-xl divide-y divide-gray-200 overflow-hidden">
                  {transactions.map((tx) => {
                    const meta = typeMeta[tx.type] || typeMeta.advance;
                    const Icon = meta.icon;

                    return (
                      <div key={tx._id} className="flex items-center gap-3 p-3 sm:p-4 hover:bg-gray-50 transition-colors">
                        <div className={`p-2 rounded-lg flex-shrink-0 ${meta.bg}`}>
                          <Icon className={`h-4 w-4 ${meta.color}`} />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-sm font-medium text-gray-900">{meta.label}</span>
                            <span className="text-xs text-gray-500">
                              {new Date(tx.date).toLocaleDateString()}
                            </span>
                          </div>
                          {tx.note && <p className="text-xs text-gray-600 mt-0.5 break-words">{tx.note}</p>}
                        </div>

                        <span className={`text-sm font-semibold flex-shrink-0 ${meta.color}`}>
                          {meta.sign} Rs. {tx.amount.toFixed(2)}
                        </span>

                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            onClick={() => onEditTransaction(tx)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title={t('Edit entry')}
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => onDeleteTransaction(tx)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title={t('Delete entry')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default StaffLedgerModal;
